import { Suspense, useEffect, lazy, ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { LoadingScreen } from './components/common/LoadingScreen'
import { OfflineIndicator } from './components/common/OfflineIndicator'
import { PWAInstallPrompt } from './components/common/PWAInstallPrompt'
import { ModernLayout } from './components/layout/ModernLayout'
import { UnauthorizedPage } from './components/common/ProtectedRoute'
import { useAuthStore } from './stores/authStore'
import { Toaster } from './components/ui/toaster'

// Lazy load route components for better performance
const HomePage = lazy(() => import('./pages/ModernHomePage'))
const AuthPage = lazy(() => import('./pages/AuthPage'))
const DatingPage = lazy(() => import('./pages/DatingPage').then(module => ({ default: module.DatingPage })))
const ModernDatingHub = lazy(() => import('./pages/ModernDatingHub'))
const ModernSwipePage = lazy(() => import('./pages/ModernSwipePage'))
const LikesSentPage = lazy(() => import('./pages/LikesSentPage'))
const MatchesPage = lazy(() => import('./pages/MatchesPage'))
const PodcastsPage = lazy(() => import('./pages/PodcastsPage'))
const EventsPage = lazy(() => import('./pages/EventsPage'))
const ChatPage = lazy(() => import('./pages/ChatPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const OrganizerPage = lazy(() => import('./pages/OrganizerPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const PageNavigator = lazy(() => import('./pages/PageNavigator'))
const TestAuth = lazy(() => import('./pages/TestAuth'))
const TailwindTest = lazy(() => import('./components/TailwindTest'))
const ScrollTest = lazy(() => import('./pages/ScrollTest'))

// Protected route component
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />
  }
  
  return <>{children}</>
}

// Public route component (redirect to home if authenticated)
function PublicRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }
  
  return <>{children}</>
}

function App() {
  useEffect(() => {
    // Handle app update prompts (service worker registration handled in main.tsx)
    const handleUpdateAvailable = () => {
      // Only show update prompt in production and not on localhost
      if (import.meta.env.PROD && !window.location.hostname.includes('localhost')) {
        if (confirm('A new version is available. Reload to update?')) {
          window.location.reload()
        }
      }
    }

    window.addEventListener('pwa:update-available', handleUpdateAvailable)
    
    return () => {
      window.removeEventListener('pwa:update-available', handleUpdateAvailable)
    }
  }, [])

  return (
    <ErrorBoundary>
      <div className="h-screen overflow-hidden bg-dark-950 text-white">
        {/* PWA Components */}
        <OfflineIndicator />
        <PWAInstallPrompt />
        
        {/* Toast Notifications */}
        <Toaster />
        
        <Routes>
          {/* Public routes */}
          <Route 
            path="/auth" 
            element={
              <PublicRoute>
                <Suspense fallback={<LoadingScreen />}>
                  <AuthPage />
                </Suspense>
              </PublicRoute>
            } 
          />
          
          {/* Protected routes with layout */}
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <ModernLayout>
                  <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/dating" element={<ModernDatingHub />} />
                      <Route path="/dating/classic" element={<DatingPage />} />
                      <Route path="/dating/swipe" element={<ModernSwipePage />} />
                      <Route path="/dating/likes" element={<LikesSentPage />} />
                      <Route path="/dating/matches" element={<MatchesPage />} />
                      <Route path="/podcasts" element={<PodcastsPage />} />
                      <Route path="/events" element={<EventsPage />} />
                      <Route path="/chat" element={<ChatPage />} />
                      <Route path="/chat/:userId" element={<ChatPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/profile/settings" element={<SettingsPage />} />
                      <Route path="/profile/@:username" element={<UserProfilePage />} />
                      <Route path="/profile/:userId" element={<UserProfilePage />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/organizer" element={<OrganizerPage />} />
                      <Route path="/events/manage" element={<OrganizerPage />} />
                      <Route path="/page-navigator" element={<PageNavigator />} />
                      <Route path="/test-auth" element={<TestAuth />} />
                      <Route path="/test-tailwind" element={<TailwindTest />} />
                      <Route path="/test-scroll" element={<ScrollTest />} />
                      <Route path="/unauthorized" element={<UnauthorizedPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </ModernLayout>
              </ProtectedRoute>
            } 
          />
        </Routes>
      </div>
    </ErrorBoundary>
  )
}

export default App